import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import router from '@/router'

// API响应接口
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  timestamp: number
}

// 分页响应接口
export interface PageResponse<T = any> {
  records: T[]
  total: number
  pages: number
  current: number
  size: number
  hasPrevious: boolean
  hasNext: boolean
  isFirst: boolean
  isLast: boolean
}

// 创建axios实例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  })

  // 请求拦截器
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 添加认证token
      const token = localStorage.getItem('api-token')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 添加请求ID用于日志追踪
      config.headers['X-Request-ID'] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      // 如果是 blob 类型的响应（文件下载），直接返回
      if (response.config.responseType === 'blob') {
        return response
      }

      const { data } = response

      // 检查业务状态码
      if (data.code === 200) {
        return response
      }

      // 处理业务错误
      const errorMessage = data.message || '请求失败'
      ElMessage.error(errorMessage)

      return Promise.reject(new Error(errorMessage))
    },
    async (error) => {
      // hideLoading()

      // 网络错误处理
      if (!error.response) {
        ElMessage.error('网络连接失败，请检查网络设置')
        return Promise.reject(error)
      }

      const { status, data } = error.response
      const userStore = useUserStore()

      // 20260527 LZH 公开认证接口（登录、注册、忘记密码、发码）的 401/403 属业务错误（账号或密码错误等），
      // 不应走"token 过期 → 续期 → 跳登录页"的统一处理流程，避免把"密码错误"误报成"登录已过期"。
      const requestUrl: string = error.config?.url || ''
      const isPublicAuthApi = (
        requestUrl.includes('/user/login') ||
        requestUrl.includes('/user/register') ||
        requestUrl.includes('/user/refresh-token') ||
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/sms/send') ||
        requestUrl.includes('/auth/email/send') ||
        requestUrl.includes('/auth/password/forgot/')
      )

      switch (status) {
        case 401: {
          // 公开认证接口的 401 是业务错误（账号或密码错误），直接透传后端文案
          if (isPublicAuthApi) {
            const message = data?.message || '账号或密码错误'
            ElMessage.error(message)
            break
          }
          // Token过期 - 尝试用refreshToken自动续期
          const refreshTokenValue = userStore.refreshToken
          if (refreshTokenValue && !error.config._retry) {
            error.config._retry = true
            try {
              await userStore.refreshAccessToken()
              // 续期成功，用新token重试原请求
              error.config.headers.Authorization = `Bearer ${localStorage.getItem('api-token')}`
              return api(error.config)
            } catch (refreshError) {
              // 续期失败，跳登录页
            }
          }
          ElMessage.error('登录已过期，请重新登录')
          userStore.clearUserData()
          clearApiToken()
          const currentPath = router.currentRoute.value.fullPath
          if (currentPath !== '/login') {
            router.push({
              path: '/login',
              query: { redirect: currentPath }
            })
          }
          break
        }

        case 403:
          ElMessage.error('权限不足，无法访问该资源')
          break

        case 404:
          ElMessage.error('请求的资源不存在')
          break

        case 429:
          ElMessage.error('请求过于频繁，请稍后再试')
          break

        case 500:
          ElMessage.error('服务器内部错误，请稍后再试')
          break

        case 502:
        case 503:
        case 504:
          ElMessage.error('服务暂时不可用，请稍后再试')
          break

        default:
          const message = data?.message || `请求失败 (${status})`
          ElMessage.error(message)
      }

      return Promise.reject(error)
    }
  )

  return instance
}

// 创建API实例
const api = createAxiosInstance()

// 设置token的方法
export const setApiToken = (token: string): void => {
  localStorage.setItem('api-token', token)
}

// 清除token的方法
export const clearApiToken = (): void => {
  localStorage.removeItem('api-token')
}

// 通用请求方法
export const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return api.get(url, config)
  },

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return api.post(url, data, config)
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return api.put(url, data, config)
  },

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return api.delete(url, config)
  },

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return api.patch(url, data, config)
  }
}

// 文件上传方法
export const uploadFile = (
  url: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<AxiosResponse<ApiResponse>> => {
  const formData = new FormData()
  formData.append('file', file)

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    }
  })
}

// 下载文件方法
export const downloadFile = (
  url: string,
  filename?: string,
  config?: AxiosRequestConfig
): Promise<void> => {
  return api.get(url, {
    ...config,
    responseType: 'blob'
  }).then(response => {
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  })
}

export { api }
export default api
